// BulkImportTravelersDialog.js - Versión final corregida

import React, { useMemo, useState, useRef } from "react";
import {
    Alert,
    Box,
    Button,
    Checkbox,
    Chip,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    FormControlLabel,
    Grid,
    MenuItem,
    Paper,
    Stack,
    TextField,
    Typography,
    Backdrop,
    CircularProgress,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    List,
    ListItem,
    ListItemText,
    ListItemIcon,
} from "@mui/material";
import {
    ExpandMore as ExpandMoreIcon,
    CheckCircle as CheckCircleIcon,
    Error as ErrorIcon,
    Warning as WarningIcon,
    Info as InfoIcon,
} from "@mui/icons-material";

const onlyDigits = (value) => String(value || "").replace(/\D/g, "");

const BulkImportTravelersDialog = ({
    open,
    onClose,
    serviceId,
    user,
    paradas = [],
    existingTravelers = [],
    onSuccess,
    showAlert,
    bulkImportMutation,
}) => {
    // ========== 1. TODOS LOS useState ==========
    const [fileName, setFileName] = useState("");
    const [dnis, setDnis] = useState([]);
    const [result, setResult] = useState(null);
    const [importCompleted, setImportCompleted] = useState(false);
    const [sendingData, setSendingData] = useState(false);
    
    // Estado para guardar los datos enviados (evita recalcular con existingTravelers actualizado)
    const [sentDataSnapshot, setSentDataSnapshot] = useState(null);
    
    // ========== 2. Config state ==========
    const [config, setConfig] = useState({
        ordserdettra: "IDA",
        ordserdetpunsubid: "",
        ordserdetpunbajid: "",
        ordserdettaxser: false,
        ordserdetdes: "",
    });

    // ========== 3. useRef ==========
    const originalDnisRef = useRef([]);

    // ========== 4. useMemo (solo para validación previa a la importación) ==========
    const validation = useMemo(() => {
        if (!dnis.length) {
            return {
                totalRecibidos: 0,
                totalInvalidos: 0,
                invalidos: [],
                totalValidos: 0,
                totalDuplicadosArchivo: 0,
                duplicadosArchivo: [],
                totalUnicosValidos: 0,
                totalDuplicadosOrden: 0,
                duplicadosOrden: [],
                totalParaEnviar: 0,
                cleanDnis: [],
            };
        }

        const normalizedDnis = dnis.map(dni => String(dni || '').replace(/\D/g, '').trim());
        const invalidos = normalizedDnis.filter(dni => !/^\d{8}$/.test(dni));
        const validos = normalizedDnis.filter(dni => /^\d{8}$/.test(dni));
        const duplicadosArchivo = validos.filter((dni, index) => validos.indexOf(dni) !== index);
        const dnisUnicosValidos = [...new Set(validos)];
        const duplicadosOrden = dnisUnicosValidos.filter(dni =>
            existingTravelers.some(
                (t) => t.perdni === dni && t.ordserdettra === config.ordserdettra
            )
        );
        const cleanDnis = dnisUnicosValidos.filter(dni => !duplicadosOrden.includes(dni));

        return {
            totalRecibidos: dnis.length,
            totalInvalidos: invalidos.length,
            invalidos: [...new Set(invalidos)],
            totalValidos: validos.length,
            totalDuplicadosArchivo: duplicadosArchivo.length,
            duplicadosArchivo: [...new Set(duplicadosArchivo)],
            totalUnicosValidos: dnisUnicosValidos.length,
            totalDuplicadosOrden: duplicadosOrden.length,
            duplicadosOrden,
            totalParaEnviar: cleanDnis.length,
            cleanDnis,
            // Guardar también los valores para el snapshot
            snapshotData: {
                totalRecibidos: dnis.length,
                totalInvalidos: invalidos.length,
                invalidos: [...new Set(invalidos)],
                totalDuplicadosOrden: duplicadosOrden.length,
                duplicadosOrden,
                totalParaEnviar: cleanDnis.length,
            }
        };
    }, [dnis, existingTravelers, config.ordserdettra]);

    // ========== 5. Variables derivadas ==========
    const canSubmit =
        !importCompleted &&
        !sendingData &&
        !bulkImportMutation?.isLoading &&
        validation.totalParaEnviar > 0 &&
        config.ordserdetpunsubid &&
        config.ordserdetpunbajid &&
        Number(config.ordserdetpunsubid) !== Number(config.ordserdetpunbajid);

    const isProcessing = sendingData || bulkImportMutation?.isLoading;

    // ========== 6. Funciones del componente ==========
    const updateConfig = (field, value) => {
        setConfig((prev) => ({
            ...prev,
            [field]: value,
        }));
    };

    const handleFile = async (event) => {
        const file = event.target.files?.[0];
        if (!file) return;

        // Resetear todos los estados al cargar nuevo archivo
        setFileName(file.name);
        setResult(null);
        setImportCompleted(false);
        setSendingData(false);
        setSentDataSnapshot(null);
        bulkImportMutation?.reset();

        try {
            const XLSX = await import("xlsx");
            const data = await file.arrayBuffer();
            const workbook = XLSX.read(data);
            const sheet = workbook.Sheets[workbook.SheetNames[0]];
            const rows = XLSX.utils.sheet_to_json(sheet, { defval: "" });

            const extracted = rows
                .map((row) => {
                    const raw =
                        row.DNI ||
                        row.dni ||
                        row.Dni ||
                        row.Documento ||
                        row.documento ||
                        row.NUMERO_DOCUMENTO ||
                        row.numero_documento;
                    return onlyDigits(raw);
                })
                .filter(Boolean);

            setDnis(extracted);
            originalDnisRef.current = extracted;
        } catch (error) {
            console.error("Error procesando archivo:", error);
            showAlert("Error al procesar el archivo Excel. Verifique el formato.", "error");
        }
    };

    const handleSubmit = () => {
        if (!canSubmit) {
            showAlert("Revise la configuración y los DNIs antes de importar.", "error");
            return;
        }

        // Crear un snapshot de los datos que vamos a enviar ANTES de limpiar
        // Esto es CRÍTICO para mostrar el resumen correcto después
        const snapshot = {
            totalRecibidos: validation.totalRecibidos,
            totalInvalidos: validation.totalInvalidos,
            invalidos: [...validation.invalidos],
            totalDuplicadosOrden: validation.totalDuplicadosOrden,
            duplicadosOrden: [...validation.duplicadosOrden],
            totalParaEnviar: validation.totalParaEnviar,
            cleanDnis: [...validation.cleanDnis],
        };
        
        setSentDataSnapshot(snapshot);
        setSendingData(true);

        const payload = {
            dnis: validation.cleanDnis,
            config: {
                ...config,
                ordserdetpunsubid: Number(config.ordserdetpunsubid),
                ordserdetpunbajid: Number(config.ordserdetpunbajid),
                crebyid: user.EmpID,
            },
        };

        bulkImportMutation.mutate(payload, {
            onSuccess: (responseData) => {
                // Guardar el resultado junto con el snapshot
                setResult({
                    ...responseData,
                    snapshot: snapshot, // Guardar el snapshot en el resultado
                });
                setImportCompleted(true);
                setSendingData(false);
                
                // CRÍTICO: Limpiar los DNIs para evitar recalculo incorrecto
                setDnis([]);
                setFileName("");
                originalDnisRef.current = [];
                
                const totalExitosos = (responseData.registrados || 0) + (responseData.reactivados || 0);
                const totalNoEncontrados = responseData.noEncontrados?.length || 0;
                
                if (totalNoEncontrados === 0) {
                    showAlert(`✅ ¡Importación exitosa! Se registraron ${totalExitosos} viajeros.`, "success");
                    
                    setTimeout(() => {
                        if (onSuccess) onSuccess(responseData);
                        handleClose();
                    }, 2000);
                } else {
                    showAlert(`⚠️ Importación parcial: ${totalExitosos} registrados, ${totalNoEncontrados} no encontrados.`, "warning");
                    if (onSuccess) onSuccess(responseData);
                }
            },
            onError: (error) => {
                console.error("Error en importación:", error);
                setSendingData(false);
                setSentDataSnapshot(null);
                
                if (error.code === 'ECONNABORTED') {
                    showAlert("⏱️ La importación está tomando más tiempo de lo esperado. Por favor, verifica en unos momentos si los viajeros fueron agregados.", "warning");
                } else {
                    showAlert(error?.response?.data?.error || "❌ Error al importar viajeros.", "error");
                }
            },
        });
    };

    const handleClose = () => {
        if (isProcessing) {
            showAlert("Espera a que termine la importación antes de cerrar.", "warning");
            return;
        }
        
        setFileName("");
        setDnis([]);
        setResult(null);
        setImportCompleted(false);
        setSendingData(false);
        setSentDataSnapshot(null);
        originalDnisRef.current = [];
        setConfig({
            ordserdettra: "IDA",
            ordserdetpunsubid: "",
            ordserdetpunbajid: "",
            ordserdettaxser: false,
            ordserdetdes: "",
        });
        
        if (bulkImportMutation?.reset) {
            bulkImportMutation.reset();
        }
        
        onClose();
    };

    // ========== 7. Renderizado de resultados (usa el snapshot, no la validación actual) ==========
    const renderDetailedResults = () => {
        if (!result) return null;

        // Usar el snapshot guardado en el momento del envío, NO la validación actual
        const snapshot = result.snapshot;
        
        if (!snapshot) {
            // Fallback si no hay snapshot (no debería ocurrir)
            return (
                <Alert severity="info" sx={{ mt: 2 }}>
                    No hay datos de resumen disponibles.
                </Alert>
            );
        }

        const enviados = snapshot.totalParaEnviar;
        const registrados = result.registrados || 0;
        const reactivados = result.reactivados || 0;
        const exitosos = registrados + reactivados;
        const noEncontrados = result.noEncontrados || [];
        const totalNoEnviados = snapshot.totalInvalidos + snapshot.totalDuplicadosOrden;

        return (
            <Box sx={{ mt: 2 }}>
                <Alert severity={noEncontrados.length === 0 ? "success" : "warning"} sx={{ mb: 2 }}>
                    <strong>{noEncontrados.length === 0 ? "✓ Importación exitosa" : "⚠ Importación parcial"}</strong>
                    <Box sx={{ mt: 1 }}>
                        <div style={{ fontWeight: 'bold', marginBottom: 8 }}>📊 Resumen final:</div>
                        <div style={{ marginLeft: 16 }}>
                            <div>📄 Total leídos en Excel: {snapshot.totalRecibidos}</div>
                            {snapshot.totalInvalidos > 0 && (
                                <div>❌ Inválidos (formato incorrecto): {snapshot.totalInvalidos}</div>
                            )}
                            {snapshot.totalDuplicadosOrden > 0 && (
                                <div>🔄 Ya existentes en este tramo: {snapshot.totalDuplicadosOrden}</div>
                            )}
                            {totalNoEnviados > 0 && (
                                <div>⏭️ Total excluidos del envío: {totalNoEnviados}</div>
                            )}
                            <div>📤 Enviados al servidor: {enviados}</div>
                            <div>✅ Registrados exitosamente: {exitosos}</div>
                            {noEncontrados.length > 0 && (
                                <div>❌ No encontrados en sistema: {noEncontrados.length}</div>
                            )}
                        </div>
                    </Box>
                </Alert>

                {/* DNIs no encontrados en el sistema */}
                {noEncontrados.length > 0 && (
                    <Accordion defaultExpanded>
                        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                            <Stack direction="row" spacing={1} alignItems="center">
                                <ErrorIcon color="error" />
                                <Typography>
                                    ❌ DNIs no encontrados en el sistema ({noEncontrados.length})
                                </Typography>
                            </Stack>
                        </AccordionSummary>
                        <AccordionDetails>
                            <Alert severity="error" sx={{ mb: 2 }}>
                                Estos DNIs no existen en la base de datos de personas.
                                Deben ser registrados primero en el módulo de personas.
                            </Alert>
                            <Paper sx={{ maxHeight: 200, overflow: 'auto', p: 1 }}>
                                <Grid container spacing={1}>
                                    {noEncontrados.map((dni, idx) => (
                                        <Grid item xs={6} sm={4} md={3} key={idx}>
                                            <Chip 
                                                label={dni} 
                                                size="small" 
                                                color="error" 
                                                variant="outlined"
                                            />
                                        </Grid>
                                    ))}
                                </Grid>
                            </Paper>
                        </AccordionDetails>
                    </Accordion>
                )}

                {/* DNIs con formato inválido */}
                {snapshot.invalidos?.length > 0 && (
                    <Accordion>
                        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                            <Stack direction="row" spacing={1} alignItems="center">
                                <WarningIcon color="warning" />
                                <Typography>
                                    ⚠️ DNIs con formato inválido ({snapshot.invalidos.length})
                                </Typography>
                            </Stack>
                        </AccordionSummary>
                        <AccordionDetails>
                            <Alert severity="warning" sx={{ mb: 2 }}>
                                Estos DNIs no cumplen con el formato requerido (8 dígitos numéricos).
                            </Alert>
                            <Paper sx={{ maxHeight: 200, overflow: 'auto', p: 1 }}>
                                <Grid container spacing={1}>
                                    {snapshot.invalidos.map((dni, idx) => (
                                        <Grid item xs={6} sm={4} md={3} key={idx}>
                                            <Chip 
                                                label={dni} 
                                                size="small" 
                                                color="warning" 
                                                variant="outlined"
                                            />
                                        </Grid>
                                    ))}
                                </Grid>
                            </Paper>
                        </AccordionDetails>
                    </Accordion>
                )}

                {/* DNIs ya existentes en la orden */}
                {snapshot.duplicadosOrden?.length > 0 && (
                    <Accordion>
                        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                            <Stack direction="row" spacing={1} alignItems="center">
                                <InfoIcon color="info" />
                                <Typography>
                                    ℹ️ Ya existentes en la orden ({snapshot.duplicadosOrden.length})
                                </Typography>
                            </Stack>
                        </AccordionSummary>
                        <AccordionDetails>
                            <Alert severity="info" sx={{ mb: 2 }}>
                                Estos DNIs ya estaban registrados en este tramo y fueron excluidos del envío.
                            </Alert>
                            <Paper sx={{ maxHeight: 200, overflow: 'auto', p: 1 }}>
                                <Grid container spacing={1}>
                                    {snapshot.duplicadosOrden.map((dni, idx) => (
                                        <Grid item xs={6} sm={4} md={3} key={idx}>
                                            <Chip 
                                                label={dni} 
                                                size="small" 
                                                color="info" 
                                                variant="outlined"
                                            />
                                        </Grid>
                                    ))}
                                </Grid>
                            </Paper>
                        </AccordionDetails>
                    </Accordion>
                )}

                {/* Registros exitosos */}
                {result.detalles && result.detalles.length > 0 && (
                    <Accordion>
                        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                            <Stack direction="row" spacing={1} alignItems="center">
                                <CheckCircleIcon color="success" />
                                <Typography>
                                    ✅ Registros exitosos ({result.detalles.length})
                                </Typography>
                            </Stack>
                        </AccordionSummary>
                        <AccordionDetails>
                            <Paper sx={{ maxHeight: 300, overflow: 'auto' }}>
                                <List dense>
                                    {result.detalles.slice(0, 20).map((detalle, idx) => (
                                        <ListItem key={idx} divider>
                                            <ListItemIcon>
                                                {detalle.accion === 'REGISTRADO' && <CheckCircleIcon color="success" />}
                                                {detalle.accion === 'REACTIVADO' && <InfoIcon color="info" />}
                                            </ListItemIcon>
                                            <ListItemText 
                                                primary={
                                                    <Typography variant="body2">
                                                        <strong>{detalle.dni}</strong> - {detalle.nombre || 'Sin nombre'}
                                                    </Typography>
                                                }
                                                secondary={
                                                    <Typography variant="caption" color="text.secondary">
                                                        {detalle.accion === 'REGISTRADO' ? 'Nuevo registro creado' : 'Registro reactivado'}
                                                    </Typography>
                                                }
                                            />
                                        </ListItem>
                                    ))}
                                    {result.detalles.length > 20 && (
                                        <ListItem>
                                            <ListItemText 
                                                secondary={`... y ${result.detalles.length - 20} más`}
                                            />
                                        </ListItem>
                                    )}
                                </List>
                            </Paper>
                        </AccordionDetails>
                    </Accordion>
                )}
            </Box>
        );
    };

    // ========== 8. Renderizado principal ==========
    return (
        <>
            <Backdrop
                sx={{
                    zIndex: (theme) => theme.zIndex.drawer + 3,
                    color: '#fff',
                    flexDirection: 'column',
                    gap: 2,
                }}
                open={isProcessing}
            >
                <CircularProgress color="primary" size={60} />
                <Typography variant="h6" sx={{ color: 'white', textAlign: 'center' }}>
                    Procesando importación...
                </Typography>
                <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.7)' }}>
                    Por favor, no cierres esta ventana
                </Typography>
            </Backdrop>

            <Dialog
                open={open}
                onClose={handleClose}
                fullWidth
                maxWidth="md"
                disableEscapeKeyDown={isProcessing}
            >
                <DialogTitle>
                    Importar viajeros desde Excel
                    {isProcessing && (
                        <CircularProgress size={24} sx={{ ml: 2, verticalAlign: 'middle' }} />
                    )}
                </DialogTitle>

                <DialogContent dividers>
                    <Stack spacing={2}>
                        <Alert severity="info">
                            El archivo debe contener una columna llamada <strong>DNI</strong>. La
                            configuración seleccionada se aplicará a todos los DNIs válidos.
                        </Alert>

                        <Paper
                            elevation={0}
                            sx={{
                                p: 2,
                                border: "1px solid",
                                borderColor: "divider",
                                borderRadius: 3,
                            }}
                        >
                            <Button
                                variant="outlined"
                                component="label"
                                disabled={isProcessing || importCompleted}
                                startIcon={isProcessing ? <CircularProgress size={20} /> : null}
                            >
                                {isProcessing ? "Procesando..." : "Seleccionar Excel"}
                                <input
                                    hidden
                                    type="file"
                                    accept=".xlsx,.xls"
                                    onChange={handleFile}
                                    disabled={isProcessing || importCompleted}
                                />
                            </Button>

                            {fileName && (
                                <Typography variant="body2" mt={1}>
                                    Archivo seleccionado: <strong>{fileName}</strong>
                                </Typography>
                            )}
                        </Paper>

                        <Grid container spacing={2}>
                            <Grid item xs={12} md={4}>
                                <TextField
                                    select
                                    fullWidth
                                    label="Tramo"
                                    value={config.ordserdettra}
                                    onChange={(e) => updateConfig("ordserdettra", e.target.value)}
                                    disabled={isProcessing || importCompleted}
                                >
                                    <MenuItem value="IDA">SUBIDA</MenuItem>
                                    <MenuItem value="VUELTA">BAJADA</MenuItem>
                                </TextField>
                            </Grid>

                            <Grid item xs={12} md={4}>
                                <TextField
                                    select
                                    fullWidth
                                    label="Punto de subida"
                                    value={config.ordserdetpunsubid}
                                    onChange={(e) => updateConfig("ordserdetpunsubid", e.target.value)}
                                    disabled={isProcessing || importCompleted}
                                >
                                    {paradas.map((p) => (
                                        <MenuItem key={p.locid} value={p.locid}>
                                            {p.locnom}
                                        </MenuItem>
                                    ))}
                                </TextField>
                            </Grid>

                            <Grid item xs={12} md={4}>
                                <TextField
                                    select
                                    fullWidth
                                    label="Punto de bajada"
                                    value={config.ordserdetpunbajid}
                                    onChange={(e) => updateConfig("ordserdetpunbajid", e.target.value)}
                                    disabled={isProcessing || importCompleted}
                                >
                                    {paradas.map((p) => (
                                        <MenuItem key={p.locid} value={p.locid}>
                                            {p.locnom}
                                        </MenuItem>
                                    ))}
                                </TextField>
                            </Grid>

                            <Grid item xs={12}>
                                <TextField
                                    fullWidth
                                    label="Observación general"
                                    value={config.ordserdetdes}
                                    onChange={(e) => updateConfig("ordserdetdes", e.target.value)}
                                    disabled={isProcessing || importCompleted}
                                />
                            </Grid>

                            <Grid item xs={12}>
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            checked={config.ordserdettaxser}
                                            onChange={(e) =>
                                                updateConfig("ordserdettaxser", e.target.checked)
                                            }
                                            disabled={isProcessing || importCompleted}
                                        />
                                    }
                                    label="Todos requieren servicio de taxi"
                                />
                            </Grid>
                        </Grid>

                        <Divider />

                        {/* Validación previa - Solo mostrar si no se ha importado y hay DNIs */}
                        {!importCompleted && dnis.length > 0 && (
                            <Box>
                                <Typography variant="subtitle1" fontWeight={800} mb={1}>
                                    Validación previa
                                </Typography>

                                <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ mb: 2 }}>
                                    <Chip 
                                        label={`📄 Leídos: ${validation.totalRecibidos}`} 
                                        color="default"
                                    />
                                    <Chip 
                                        label={`✅ Válidos: ${validation.totalValidos}`} 
                                        color="success"
                                    />
                                    <Chip 
                                        label={`❌ Inválidos: ${validation.totalInvalidos}`} 
                                        color="error"
                                    />
                                    <Chip 
                                        label={`📋 Duplicados archivo: ${validation.totalDuplicadosArchivo}`} 
                                        color="warning"
                                    />
                                    <Chip 
                                        label={`🔄 Ya existen: ${validation.totalDuplicadosOrden}`} 
                                        color="info"
                                    />
                                    <Chip 
                                        label={`📤 A enviar: ${validation.totalParaEnviar}`} 
                                        color="primary"
                                        variant={validation.totalParaEnviar > 0 ? "filled" : "outlined"}
                                    />
                                </Stack>

                                {validation.invalidos.length > 0 && (
                                    <Alert severity="error" sx={{ mt: 2 }}>
                                        <strong>DNI inválidos (formato incorrecto):</strong>{" "}
                                        {validation.invalidos.join(", ")}
                                    </Alert>
                                )}

                                {validation.duplicadosOrden.length > 0 && (
                                    <Alert severity="info" sx={{ mt: 2 }}>
                                        <strong>Ya existen en este tramo:</strong>{" "}
                                        {validation.duplicadosOrden.join(", ")}
                                    </Alert>
                                )}

                                {validation.totalParaEnviar === 0 && validation.totalRecibidos > 0 && (
                                    <Alert severity="warning" sx={{ mt: 2 }}>
                                        ⚠️ No hay DNIs válidos para enviar. Todos fueron filtrados por 
                                        formato inválido o porque ya existen en la orden.
                                    </Alert>
                                )}
                            </Box>
                        )}

                        {/* Resultados detallados después de la importación */}
                        {importCompleted && renderDetailedResults()}
                    </Stack>
                </DialogContent>

                <DialogActions>
                    <Button
                        onClick={handleClose}
                        disabled={isProcessing}
                    >
                        {importCompleted ? "Cerrar" : "Cancelar"}
                    </Button>
                    
                    {!importCompleted && (
                        <Button
                            variant="contained"
                            onClick={handleSubmit}
                            disabled={!canSubmit || isProcessing}
                            startIcon={isProcessing ? <CircularProgress size={20} /> : null}
                        >
                            {isProcessing ? "Enviando..." : "Importar viajeros"}
                        </Button>
                    )}
                    
                    {importCompleted && (
                        <Button
                            variant="outlined"
                            onClick={() => {
                                setImportCompleted(false);
                                setResult(null);
                                setSentDataSnapshot(null);
                                setDnis([]);
                                setFileName("");
                                originalDnisRef.current = [];
                                bulkImportMutation?.reset();
                            }}
                        >
                            Nueva importación
                        </Button>
                    )}
                </DialogActions>
            </Dialog>
        </>
    );
};

export default BulkImportTravelersDialog;